/**
 * ManageBooking — the public self-service page the confirmation/reminder
 * comms link to (route `appointment.manage`, unguarded like queue.join).
 * Closes APT-09: the reschedule/cancel links in every template now land on a
 * real page. The lookup still reads through GM15's SHIPPED typed-client fn
 * (lookupBooking); in preview the appointments seed layer answers the same
 * route while kit mock families are dist-gated.
 *
 * CHANGES GO THROUGH THE MANAGE SURFACE, WITH THE STEP-UP. The older
 * `/v1/public/bookings/manage/:id` cancel/reschedule now answer
 * `403 verificationRequired` without the verification code, exactly like the
 * manage surface always has. So cancel and reschedule here use the same typed
 * `manageSurface` operations `manage/dialogs.tsx` uses: open a manage session
 * for the booking, and when the SERVER refuses with `verificationRequired`,
 * send the one-time code, confirm it, and retry once. The page never decides
 * an authority question itself.
 *
 * ⛔ Session and elevation tokens live in the module closure below, never in
 * React state, a `data-*` attribute, a URL or storage — the same vault rule as
 * `manage/manage-session.ts`. Components only ever see the masked target.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, DayStrip, Skeleton, Input, OtpInput, T } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import { isApiError, manageSurface } from "@v0y4ge/api-client";
import { newIdempotencyKey } from "@v0y4ge/api-contracts";
import type { ManageViewDto } from "@v0y4ge/api-contracts";
import type { Booking } from "@v0y4ge/domain-types";
import { lookupBooking } from "./appointments-api";

/* ── the vault (module closure, never React state) ───────────────────── */

const vault = { bookingId: "", session: "", elevation: "" };

function credentials(): { sessionToken: string; elevationToken?: string } {
  return vault.elevation ? { sessionToken: vault.session, elevationToken: vault.elevation } : { sessionToken: vault.session };
}

function clearVault(): void {
  vault.bookingId = "";
  vault.session = "";
  vault.elevation = "";
}

/** Thrown when the visitor dismisses the code step. Never shown. */
class StepUpAbandoned extends Error {
  constructor() {
    super("step-up abandoned");
    this.name = "StepUpAbandoned";
  }
}

interface Verify {
  /** Only ever the masked form the server returned. */
  maskedTarget: string;
  channel: "email" | "sms" | null;
  sending: boolean;
  resendAt: number;
  errorKey: "unavailable" | "exhausted" | "wrong" | "generic" | null;
}

interface Slot {
  start_utc: string;
  end_utc: string;
  staff_id: string | null;
  staff_name: string | null;
}

function slotCivilDay(timeZone: string, instant = new Date()): string {
  const p = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(instant);
  return ["year", "month", "day"].map(k => p.find(x => x.type === k)!.value).join("-");
}
function slotDays(timeZone: string): string[] {
  if (!timeZone) return [];
  const today = slotCivilDay(timeZone);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today + "T12:00:00Z"); d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

export default function ManageBooking() {
  const t = useT();
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token")?.trim() ?? "");
  const [ref, setRef] = useState(() => new URLSearchParams(window.location.search).get("booking")?.trim() ?? "");
  const [booking, setBooking] = useState<Booking | null>(null);
  /** The manage view the server last returned — carries `version` and the post-change state. */
  const [view, setView] = useState<ManageViewDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"view" | "reschedule">("view");
  const [date, setDate] = useState("");
  const [slotZone, setSlotZone] = useState("");
  const [slotError, setSlotError] = useState<string | null>(null);
  const [savingSlot, setSavingSlot] = useState<Slot | null>(null);
  const slotRequest = useRef(0);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [verify, setVerify] = useState<Verify | null>(null);
  const [code, setCode] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const days = useMemo(() => slotDays(slotZone), [slotZone]);

  /** The promise an operation parks on while the code step is on screen. */
  const gate = useRef<{ promise: Promise<void>; resolve: () => void; reject: (e: unknown) => void } | null>(null);
  /** One idempotency key per reschedule intent (minted when the slot list opens). */
  const rescheduleIntent = useRef("");
  const viewRef = useRef<ManageViewDto | null>(null);

  useEffect(() => {
    const forget = () => clearVault();
    window.addEventListener("pagehide", forget);
    return () => { slotRequest.current++; window.removeEventListener("pagehide", forget); clearVault(); };
  }, []);

  useEffect(() => {
    if (!verify || verify.resendAt <= now) return;
    const timer = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(timer);
  }, [verify, now]);

  const applyView = (next: ManageViewDto) => { viewRef.current = next; setView(next); };

  /** The booking id is the manage link's credential; it buys a masked session only. */
  const ensureSession = async (bookingId: string) => {
    if (vault.session && vault.bookingId === bookingId) return;
    const response = await manageSurface.createManageSession({ token: bookingId });
    vault.bookingId = bookingId;
    vault.session = response.data.session_token;
    vault.elevation = "";
    applyView(response.data.view);
  };

  const openGate = (): Promise<void> => {
    if (gate.current) return gate.current.promise;
    let resolve!: () => void;
    let reject!: (e: unknown) => void;
    const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
    gate.current = { promise, resolve, reject };
    setCode("");
    setVerify({ maskedTarget: "", channel: null, sending: false, resendAt: 0, errorKey: null });
    return promise;
  };

  /**
   * Run one manage operation, honouring the server's refusals: a dead session
   * re-opens once, and `403 verificationRequired` runs the code step and retries
   * once. Anything else is the caller's error.
   */
  const withManage = async <R,>(fn: (c: { sessionToken: string; elevationToken?: string }) => Promise<R>): Promise<R> => {
    const bookingId = booking?.id ?? ref.trim();
    await ensureSession(bookingId);
    let reopened = false;
    let stepped = false;
    for (;;) {
      try {
        return await fn(credentials());
      } catch (error) {
        if (!isApiError(error)) throw error;
        if (!reopened && (error.code === "sessionExpired" || error.code === "sessionRequired")) {
          reopened = true;
          vault.session = "";
          vault.elevation = "";
          await ensureSession(bookingId);
          continue;
        }
        if (!stepped && error.code === "verificationRequired") {
          stepped = true;
          vault.elevation = "";
          await openGate();
          continue;
        }
        throw error;
      }
    }
  };

  const requestCode = async () => {
    setVerify((v) => (v ? { ...v, sending: true, errorKey: null } : v));
    try {
      const response = await manageSurface.requestManageVerification(credentials(), verify?.channel ? { channel: verify.channel } : {});
      const data = response.data;
      setCode("");
      setNow(Date.now());
      setVerify((v) => (v ? { ...v, sending: false, maskedTarget: data.masked_target, channel: data.channel, resendAt: Date.now() + data.retry_after_seconds * 1000 } : v));
    } catch (error) {
      const c = isApiError(error) ? error.code : "";
      setVerify((v) => (v ? { ...v, sending: false, errorKey: c === "verificationUnavailable" ? "unavailable" : c === "otpExhausted" ? "exhausted" : "generic" } : v));
    }
  };

  const submitCode = async () => {
    setVerify((v) => (v ? { ...v, sending: true, errorKey: null } : v));
    try {
      const response = await manageSurface.confirmManageVerification(credentials(), { code });
      vault.elevation = response.data.elevated_token;
      setVerify(null);
      setCode("");
      gate.current?.resolve();
      gate.current = null;
    } catch (error) {
      const c = isApiError(error) ? error.code : "";
      setVerify((v) => (v ? { ...v, sending: false, errorKey: c === "otpExhausted" ? "exhausted" : "wrong" } : v));
    }
  };

  const abandonStepUp = () => {
    setVerify(null);
    setCode("");
    gate.current?.reject(new StepUpAbandoned());
    gate.current = null;
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) {
      if (e instanceof StepUpAbandoned) return;
      if (isApiError(e) && e.code === "bookingVersionConflict") {
        setErr(t("appointments.manage.versionConflict", "This booking just changed. Check the details and try again."));
        try { applyView((await manageSurface.getManageView(credentials())).data); } catch { /* the next action re-opens */ }
        return;
      }
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };
  const lookup = () => run(async () => { clearVault(); viewRef.current = null; setView(null); setBooking(await lookupBooking(ref.trim(), token)); setMode("view"); });
  const loadSlots = (d?: string) => {
    const request = ++slotRequest.current;
    setDate(d || ""); setSlots(null); setSlotError(null); setErr(null);
    void withManage((c) => manageSurface.getManageRescheduleSlots(c, d ? { from: d } : {}))
      .then((response) => {
        if (request !== slotRequest.current) return;
        const zone = response.data.timezone;
        if (typeof zone !== "string" || !zone.trim()) throw new Error("Availability did not provide a timezone. Please retry.");
        let today: string;
        try { today = slotCivilDay(zone); } catch { throw new Error("Availability returned an invalid timezone. Please retry."); }
        const selected = d || today;
        setSlotZone(zone); setDate(selected);
        setSlots(response.data.slots.filter(s => slotCivilDay(zone, new Date(s.start_utc)) === selected));
      })
      .catch((e) => {
        if (request !== slotRequest.current) return;
        if (e instanceof StepUpAbandoned) { setMode("view"); return; }
        setSlotError(e instanceof Error ? e.message : String(e));
      });
  };
  const startReschedule = () => { rescheduleIntent.current = newIdempotencyKey(); setSlotZone(""); setMode("reschedule"); loadSlots(); };
  const doCancel = () => {
    const intent = newIdempotencyKey();
    return run(async () => {
      const response = await withManage((c) => manageSurface.cancelManageBooking(c, { idempotency_key: intent, version: viewRef.current?.version ?? "" }));
      applyView(response.data.view);
    });
  };
  const doReschedule = (s: Slot) => {
    if (busy || !slots?.includes(s)) return;
    setSavingSlot(s);
    return run(async () => {
    const hold = await withManage((c) => manageSurface.holdManageRescheduleSlot(c, { start_utc: s.start_utc, ...(s.staff_id ? { staff_id: s.staff_id } : {}) }));
    const holdId = hold.data.hold_id;
    try {
      const response = await withManage((c) => manageSurface.confirmManageReschedule(c, { hold_id: holdId, idempotency_key: rescheduleIntent.current, version: viewRef.current?.version ?? "" }));
      applyView(response.data.view);
      // A reschedule rotates the session generation: the next change opens a fresh one.
      vault.session = "";
      vault.elevation = "";
      setMode("view");
    } catch (e) {
      // A dangling hold blocks that slot for everyone until it expires.
      void manageSurface.releaseManageRescheduleSlot(credentials(), { hold_id: holdId }).catch(() => undefined);
      throw e;
    }
    }).finally(() => setSavingSlot(null));
  };

  const status = view?.status ?? booking?.status;
  const startUtc = view?.schedule.start_utc ?? booking?.startUtc ?? "";
  const endUtc = view?.schedule.end_utc ?? booking?.endUtc ?? "";
  const cancelled = status === "cancelled";
  const resendIn = verify ? Math.max(0, Math.ceil((verify.resendAt - now) / 1000)) : 0;
  const verifyError = !verify?.errorKey ? null
    : verify.errorKey === "unavailable" ? t("appointments.manage.verifyUnavailable", "We can't send a code for this booking. Contact us to change it.")
    : verify.errorKey === "exhausted" ? t("appointments.manage.verifyExhausted", "Too many attempts. Wait a few minutes and try again.")
    : verify.errorKey === "wrong" ? t("appointments.manage.verifyWrong", "That code didn't work. Check it and try again.")
    : t("appointments.manage.verifyGeneric", "Something went wrong. Try again.");
  const card: React.CSSProperties = { background: "#fff", border: `1px solid ${T.n6}`, borderRadius: 14, padding: 22, display: "flex", flexDirection: "column", gap: 14 };
  return (
    <div style={{ minHeight: "100vh", background: "var(--jrni-color-surface-canvas, #F5F6F8)", display: "flex", justifyContent: "center", padding: "48px 16px", fontFamily: "var(--jrni-font-family-sans, sans-serif)" }} data-screen-label="Manage booking (public)">
      <div style={{ width: "min(460px, 100%)", display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <div style={{ fontSize: 21, fontWeight: 750, letterSpacing: "-0.01em" }}>{t("appointments.manage.manageYourBooking", "Manage your booking")}</div>
          <div style={{ fontSize: 13, color: T.soft, marginTop: 4 }}>{t("appointments.manage.subtitle", "Look up your appointment with the reference from your confirmation.")}</div>
        </div>
        {!token ? (
          <div style={card} data-testid="manage-missing-token">
            <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("appointments.manage.linkRequired", "Open your emailed manage link")}</div>
            <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.6 }}>{t("appointments.manage.linkRequiredBody", "For your security, bookings can only be changed from the personal manage link in your confirmation or reminder email.")}</div>
          </div>
        ) : !booking ? (
          <div style={card}>
            <Input value={ref} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRef(e.target.value)} placeholder={t("appointments.manage.bookingReference", "Booking reference (e.g. APT-2417)")} data-testid="manage-ref" />
            {err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{err}</div> : null}
            <Button disabled={busy || ref.trim().length < 3} onClick={lookup} data-testid="manage-lookup">{busy ? t("appointments.manage.looking", "Looking up…") : t("appointments.manage.findMyBooking", "Find my booking")}</Button>
          </div>
        ) : (
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 15.5, fontWeight: 750 }}>{t("appointments.manage.appointment", "Appointment")}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: cancelled ? "#FDECEC" : "#EAF7F0", color: cancelled ? "#B91C1C" : "#027D47" }}>{cancelled ? t("appointments.manage.cancelled", "Cancelled") : t("appointments.manage.confirmedBadge", "Confirmed")}</span>
            </div>
            <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.7 }}>
              {new Date(startUtc).toLocaleString()}–{new Date(endUtc).toLocaleTimeString()}<br />
              {t("appointments.manage.reference", "Reference")}: {booking.bookingRef}
            </div>
            {err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{err}</div> : null}
            {verify ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="manage-verify">
                <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("appointments.manage.verifyTitle", "Confirm it's you")}</div>
                {/* ONLY the masked target. Never the address or number itself. */}
                <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.6 }}>{verify.maskedTarget ? t("appointments.manage.verifySent", "Enter the code we sent to {target}.", { target: verify.maskedTarget }) : t("appointments.manage.verifyIntro", "To change this booking, we'll send a one-time code to the contact details on it.")}</div>
                {verifyError ? <div style={{ fontSize: 12.5, color: T.red1 }}>{verifyError}</div> : null}
                {verify.maskedTarget ? <OtpInput value={code} onChange={setCode} error={verify.errorKey === "wrong"} testid="manage-verify-code" /> : null}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {verify.maskedTarget ? (
                    <Button disabled={verify.sending || code.length < 4} onClick={() => void submitCode()} data-testid="manage-verify-submit">{verify.sending ? t("appointments.manage.verifying", "Checking…") : t("appointments.manage.verifySubmit", "Verify")}</Button>
                  ) : (
                    <Button disabled={verify.sending} onClick={() => void requestCode()} data-testid="manage-verify-send">{verify.sending ? t("appointments.manage.sendingCode", "Sending…") : t("appointments.manage.sendCode", "Send code")}</Button>
                  )}
                  {verify.maskedTarget ? (
                    <Button variant="secondary" disabled={verify.sending || resendIn > 0} onClick={() => void requestCode()} data-testid="manage-verify-resend">{resendIn > 0 ? t("appointments.manage.resendIn", "Send a new code in {n}s", { n: resendIn }) : t("appointments.manage.resendCode", "Send a new code")}</Button>
                  ) : null}
                  <Button hierarchy="tertiary" disabled={verify.sending} onClick={abandonStepUp} data-testid="manage-verify-cancel">{t("appointments.manage.notNow", "Not now")}</Button>
                </div>
              </div>
            ) : null}
            {!verify && !cancelled && mode === "view" ? (
              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="secondary" disabled={busy} onClick={startReschedule} data-testid="manage-reschedule">{t("appointments.manage.reschedule", "Reschedule")}</Button>
                <Button hierarchy="tertiary" disabled={busy} onClick={doCancel} data-testid="manage-cancel">{t("appointments.manage.cancelBooking", "Cancel booking")}</Button>
              </div>
            ) : null}
            {!verify && !cancelled && mode === "reschedule" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {slotZone ? <><DayStrip days={days.map((d, i) => ({ value: d, label: i === 0 ? t("appointments.book.today", "Today") : d.slice(5), disabled: busy }))} value={date} onChange={loadSlots} /><div style={{ fontSize: 12, color: T.soft }}>Times in {slotZone}</div></> : slotError ? null : <div role="status" aria-label="Loading days"><Skeleton width="100%" height={50} variant="rect" /></div>}
                {slotError ? <div role="alert" style={{ fontSize: 12.5, color: T.red1 }}>{slotError} <Button onClick={() => loadSlots(date || undefined)}>Retry</Button></div> : !slots ? <div role="status" aria-label="Loading availability" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} width="100%" height={48} variant="rect" />)}</div> : slots.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.sheet.noSlots", "No open times for this day — try another day.")}</div> : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(136px, 1fr))", gap: 8 }}>
                    {slots.map((s) => (
                      /* ui-raw: appointment slot button — SlotPicker's pattern (group 9) */
                      <Button variant="secondary" key={`${s.start_utc}-${s.staff_id ?? ""}`} disabled={busy} aria-busy={busy && savingSlot === s} onClick={() => doReschedule(s)} data-testid="manage-slot" style={{ flexDirection: "column", gap: 2, height: "auto", minHeight: 52, padding: "8px 6px", borderRadius: 8, border: `1.5px solid ${T.n5}`, background: "#fff", cursor: "pointer" }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{busy && savingSlot === s ? t("appointments.manage.saving", "Saving…") : new Intl.DateTimeFormat(undefined, { timeZone: slotZone, hour: "2-digit", minute: "2-digit", timeZoneName: "short" }).format(new Date(s.start_utc))}</div>
                        {s.staff_name ? <div style={{ fontSize: 10.5, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.staff_name}</div> : null}
                      </Button>
                    ))}
                  </div>
                )}
                <Button hierarchy="tertiary" size="sm" disabled={busy} onClick={() => { slotRequest.current++; setMode("view"); }}>{t("appointments.manage.keepCurrentTime", "Keep current time")}</Button>
              </div>
            ) : null}
            {cancelled ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("appointments.manage.cancelledNote", "This booking is cancelled. You can book again any time.")}</div> : null}
          </div>
        )}
      </div>
    </div>
  );
}
